import {
    DataGrid,
    GridColDef,
    GridToolbar,
    GridCsvExportOptions,
    GridRenderCellParams,
    GridRowParams,
} from '@mui/x-data-grid';
import { Shipment } from 'types';
import { DataGridPlaceholder } from 'components/DataGridPlaceholder/DataGridPlaceholder';
import DeviceStatusSummaryCell from 'components/DataGridCells/DeviceStatusSummaryCell/DeviceStatusSummaryCell';
import { timestampToDateFormatter } from 'components/DataGridCells/data-grid-formatters';
import { statusChipToTextFormatter } from './data-grid-formatters';
import { useState } from 'react';
import ShipmentModal from 'views/Monitors/modals/ShipmentModal/ShipmentModal';
import { useUi } from 'hooks/useUi';
import { CopyShipmentDevicesCell } from './CopyShipmentDevicesCell/CopyShipmentDevicesCell';
import { TrackingLink } from '../../TrackingLink/TrackingLink';

export interface ShipmentsTableProps {
    /** List of supplier's shipments to display in the shipments table. */
    shipments: Shipment[];
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

/** The Shipments tab on the Monitor Portfolio page. */
export default function ShipmentsTable(props: ShipmentsTableProps) {
    const { ui } = useUi();
    const columns: GridColDef[] = [
        {
            field: 'time',
            headerName: 'Ordered',
            valueFormatter: timestampToDateFormatter,
            flex: 1.25,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'qty',
            headerName: 'Quantity',
            flex: 0.5,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'type_of_order',
            headerName: 'Purchase Type',
            flex: 1,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'statusSummary',
            headerName: 'Status',
            flex: 1.5,
            valueFormatter: statusChipToTextFormatter, //TODO this needs to be updated.
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <DeviceStatusSummaryCell
                        viewAsSupplier={props.viewAsSupplier}
                        shipment={params.row}
                    />
                );
            },
            sortable: false,
        },
        {
            field: 'product_name',
            headerName: 'Product',
            flex: 1.5,
            sortingOrder: ['asc', 'desc'],
        },
        {
            field: 'trackingLink',
            headerName: 'Shipping Destination',
            flex: 2,
            sortingOrder: ['asc', 'desc'],
            renderCell: (params) => {
                const trackingLink = params.row.trackingLink;
                const deliveryCarrier = params.row.delivery_carrier;

                return (
                    <TrackingLink
                        trackingLink={trackingLink}
                        deliveryCarrier={deliveryCarrier}
                    />
                );
            },
        },
        {
            field: 'copy',
            headerName: 'Copy Devices to Clipboard',
            sortable: false,
            disableExport: true,
            renderCell: (params: GridRenderCellParams<any, Shipment, any>) => {
                const devices = params.row.devices;
                return <CopyShipmentDevicesCell devices={devices} />;
            },
        },
    ];

    const csvOptions: GridCsvExportOptions = {
        fileName: 'tank-utility-shipments',
    };

    const [modalOpen, setModalOpen] = useState(false);
    const [rowClicked, setRowClicked] = useState<any>(undefined);

    const handleClick = (params: GridRowParams) => {
        setModalOpen(true);
        setRowClicked(params.row);
    };

    return (
        <div data-testid="ShipmentsTable">
            {ui.animatingViewTransition ? (
                <DataGridPlaceholder />
            ) : (
                <>
                    <DataGrid
                        autoHeight
                        pageSize={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        getRowHeight={() => 'auto'}
                        columns={columns}
                        rows={props.shipments}
                        getRowId={(shipment) => shipment.$id}
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{ toolbar: { csvOptions } }}
                        onRowClick={(params) => handleClick(params)}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'time', sort: 'desc' }],
                            },
                        }}
                        onCellClick={(params, e) => {
                            if (!!params && params.field === 'trackingLink') {
                                // Don't pull up the shipment modal if the user clicks on the tracking link.
                                e.stopPropagation();
                            }
                        }}
                        disableDensitySelector
                        disableColumnMenu
                        disableColumnFilter
                    />

                    <ShipmentModal
                        modalOpen={modalOpen}
                        setModalOpen={setModalOpen}
                        shipment={rowClicked}
                    />
                </>
            )}
        </div>
    );
}
